import { mkdir, readFile, unlink, writeFile } from "node:fs/promises";
import { join } from "node:path";
import type { MemoryRef, MemoryStore } from "./memory-store.js";

export interface FileMemoryStoreOptions {
  baseDir?: string;
}

function resolveFilePath(baseDir: string, ref: MemoryRef): string {
  const runId = ref.parent_run_id ?? "unknown";
  return join(baseDir, `${runId}_${ref.id}.json`);
}

/**
 * File-backed MemoryStore. Persists memory content as JSON files under
 * `<baseDir>/` (default: `.agent-logs/memories/`).
 */
export class FileMemoryStore implements MemoryStore {
  readonly baseDir: string;

  constructor(options?: FileMemoryStoreOptions) {
    this.baseDir = options?.baseDir ?? ".agent-logs/memories";
  }

  async save(ref: MemoryRef, content: unknown): Promise<void> {
    await mkdir(this.baseDir, { recursive: true });
    const filePath = resolveFilePath(this.baseDir, ref);
    await writeFile(filePath, JSON.stringify(content, null, 2), "utf-8");
  }

  async load(ref: MemoryRef): Promise<unknown | null> {
    const filePath = resolveFilePath(this.baseDir, ref);
    try {
      const raw = await readFile(filePath, "utf-8");
      return JSON.parse(raw) as unknown;
    } catch (error) {
      if (
        error instanceof Error &&
        "code" in error &&
        (error as NodeJS.ErrnoException).code === "ENOENT"
      ) {
        return null;
      }
      throw error;
    }
  }

  async delete(ref: MemoryRef): Promise<void> {
    const filePath = resolveFilePath(this.baseDir, ref);
    try {
      await unlink(filePath);
    } catch (error) {
      if (
        error instanceof Error &&
        "code" in error &&
        (error as NodeJS.ErrnoException).code === "ENOENT"
      ) {
        return;
      }
      throw error;
    }
  }
}
